import {
  Activity01Icon,
  BalanceScaleIcon,
  BinocularsIcon,
  Clock01Icon,
  Download01Icon,
  FileAttachmentIcon,
  FileEditIcon,
  Shield01Icon,
} from '@hugeicons-pro/core-stroke-rounded'

type GraphToolArgMeta = {
  key: string
  path?: string[]
  type: 'string' | 'textarea' | 'number' | 'enum'
  required?: boolean
  options?: Array<{ label: string; value: string }>
  min?: number
  max?: number
  helper?: string
}

export type GraphToolMeta = {
  name: string
  label: string
  icon: typeof Activity01Icon
  inputSchema: {
    type: 'object'
    properties: Record<string, unknown>
    required?: string[]
  }
  group?: 'Data' | 'Output'
  args: GraphToolArgMeta[]
}

const periodOptions = [
  { label: 'Today', value: 'today' },
  { label: 'Yesterday', value: 'yesterday' },
  { label: 'Last 7 days', value: 'last_7' },
  { label: 'Last 30 days', value: 'last_30' },
]

const outputFormatOptions = [
  { label: 'Plain text', value: 'text' },
  { label: 'Markdown', value: 'markdown' },
  { label: 'JSON', value: 'json' },
  { label: 'CSV', value: 'csv' },
]

const markdownStyleOptions = [
  { label: 'GFM', value: 'gfm' },
  { label: 'Plain', value: 'plain' },
]

export const graphToolMeta = [
  {
    name: 'read_file_attachment',
    label: 'Read file attachment',
    icon: FileAttachmentIcon,
    inputSchema: {
      type: 'object',
      properties: {
        attachment_id: { type: 'string' },
        mode: { enum: ['text', 'json', 'metadata_only'] },
        max_bytes: { type: 'number' },
      },
      required: ['attachment_id', 'mode'],
    },
    args: [
      { key: 'attachment_id', type: 'string', required: true, helper: 'Use an attachment id from the trigger context.' },
      {
        key: 'mode',
        type: 'enum',
        required: true,
        options: [
          { label: 'Text', value: 'text' },
          { label: 'JSON', value: 'json' },
          { label: 'Metadata only', value: 'metadata_only' },
        ],
      },
      { key: 'max_bytes', type: 'number', min: 1, max: 5000000, helper: 'Defaults to 500000, hard-capped at 5000000.' },
    ],
  },
  {
    name: 'write_file_output',
    label: 'Write file output',
    icon: Download01Icon,
    inputSchema: {
      type: 'object',
      properties: {
        content: { type: 'string' },
        filename: { type: 'string' },
        mime_type: { type: 'string' },
      },
      required: ['content', 'filename'],
    },
    args: [
      { key: 'content', type: 'string', required: true },
      { key: 'filename', type: 'string', required: true, helper: 'Example: report.csv' },
      { key: 'mime_type', type: 'string', helper: 'Optional; inferred from filename when omitted.' },
    ],
  },
  {
    name: 'get_campaign_performance',
    label: 'Get campaign performance',
    icon: Activity01Icon,
    inputSchema: {
      type: 'object',
      properties: {
        period: { enum: ['today', 'yesterday', 'last_7', 'last_30'] },
        campaignId: { type: 'string' },
      },
    },
    args: [
      { key: 'period', type: 'enum', options: periodOptions },
      { key: 'campaignId', type: 'string', helper: 'Leave blank for all campaigns' },
    ],
  },
  {
    name: 'get_budget_status',
    label: 'Get budget status',
    icon: BinocularsIcon,
    inputSchema: {
      type: 'object',
      properties: { campaignId: { type: 'string' } },
    },
    args: [
      { key: 'campaignId', type: 'string', helper: 'Leave blank for all campaigns' },
    ],
  },
  {
    name: 'get_creative_status',
    label: 'Get creative status',
    icon: Shield01Icon,
    inputSchema: {
      type: 'object',
      properties: { campaignId: { type: 'string' } },
      required: ['campaignId'],
    },
    args: [
      { key: 'campaignId', type: 'string', required: true },
    ],
  },
  {
    name: 'get_recent_command_runs',
    label: 'Get recent command runs',
    icon: Clock01Icon,
    inputSchema: {
      type: 'object',
      properties: {
        commandId: { type: 'string' },
        limit: { type: 'number' },
      },
    },
    args: [
      { key: 'commandId', type: 'string' },
      { key: 'limit', type: 'number', min: 1, max: 50 },
    ],
  },
  {
    name: 'format_currency',
    label: 'Format as currency',
    icon: BalanceScaleIcon,
    inputSchema: {
      type: 'object',
      properties: { cents: { type: 'number' } },
      required: ['cents'],
    },
    args: [
      { key: 'cents', type: 'number', required: true },
    ],
  },
  {
    name: 'format_percent',
    label: 'Format as percent',
    icon: BalanceScaleIcon,
    inputSchema: {
      type: 'object',
      properties: {
        ratio: { type: 'number' },
        decimals: { type: 'number' },
      },
      required: ['ratio'],
    },
    args: [
      { key: 'ratio', type: 'number', required: true },
      { key: 'decimals', type: 'number', min: 0, max: 6 },
    ],
  },
  {
    name: 'format_output',
    label: 'Format output',
    icon: FileEditIcon,
    group: 'Output',
    inputSchema: {
      type: 'object',
      properties: {
        input: { type: 'string' },
        format: { enum: ['text', 'markdown', 'json', 'csv'] },
        options: {
          type: 'object',
          properties: {
            csv_delimiter: { type: 'string' },
            json_indent: { type: 'number' },
            markdown_style: { enum: ['gfm', 'plain'] },
          },
        },
      },
      required: ['input', 'format'],
    },
    args: [
      { key: 'input', type: 'textarea', required: true, helper: 'Use a prior step token, such as ${step1}.' },
      { key: 'format', type: 'enum', required: true, options: outputFormatOptions },
      { key: 'csv_delimiter', path: ['options', 'csv_delimiter'], type: 'string', helper: 'Defaults to comma' },
      { key: 'json_indent', path: ['options', 'json_indent'], type: 'number', min: 0, max: 10 },
      { key: 'markdown_style', path: ['options', 'markdown_style'], type: 'enum', options: markdownStyleOptions },
    ],
  },
] satisfies GraphToolMeta[]

export const graphToolMetaByName = new Map(graphToolMeta.map((tool) => [tool.name, tool]))
